import { useEffect, useState } from 'react';
import { CarRentalSection, LeasingSection, ChauffeurSection, CoachSection, TruckSection, UsedCarsSection } from '@/components/services/ServiceSections';
import { BrandHero, CtaBand, CheckList, SectionHeader } from '@/components/brand/BrandKit';

const HERO_IMG = "/generated/services-hero.jpg";

const filterTabs = [
  { id: "all", label: "All" },
  { id: "rental", label: "Rental" },
  { id: "leasing", label: "Leasing" },
  { id: "chauffeur", label: "Chauffeur" },
  { id: "coach", label: "Coach" },
  { id: "freight", label: "Freight" },
  { id: "used", label: "Used Cars" },
];

export default function ServicesPage() {
  const [activeFilter, setActiveFilter] = useState("all");

  useEffect(() => { document.title = "Mobility Solutions | Eurogulf Mobility Group"; }, []);

  const show = (category) => activeFilter === "all" || activeFilter === category;

  return (
    <div data-testid="services-page">
      <BrandHero testid="services-hero" chip="Complete Mobility Solutions" title="Every mode of mobility. One trusted partner."
        lead="From individual travellers to enterprise fleets, Eurogulf Mobility Group delivers the UAE's most comprehensive transport solutions under one roof."
        image={HERO_IMG} imageAlt="Eurogulf Mobility Group coach, chauffeur sedan, SUV and van on a Dubai highway" position="center 65%"
        ctas={[{ label: 'Talk to Our Team', href: '/contact', testid: 'services-hero-cta' }, { label: 'Book a Chauffeur', href: '/book-chauffeur', variant: 'outline' }]} />

      {/* OUR VALUE PROPOSITION — BRAND FILM */}
      <section data-testid="value-prop-video" className="bg-white py-14 sm:py-20 border-b border-black/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <div>
            <SectionHeader compact chip="Our Value Proposition" title="Whatever moves you, we keep you moving."
              desc="From a single airport pickup to a nationwide staff-transport programme, Eurogulf Mobility Group brings rental, leasing, chauffeur, bus & coach, commercial fleet and used-car remarketing into one connected platform, so every journey is covered by one trusted partner." />
            <CheckList className="mt-8" items={["One partner for every mode of mobility", "All seven Emirates, supported 24/7", "50 years of trust, since 1976"]} />
          </div>
          <div className="mx-auto w-full max-w-[400px] overflow-hidden rounded-xl border border-black/[0.06] bg-[#121212] aspect-[4/5]">
            <video
              data-testid="value-prop-video-player"
              className="h-full w-full object-cover"
              src="/managed/keeps-you-moving.mp4"
              poster="/managed/keeps-you-moving-poster.jpg"
              autoPlay
              muted
              loop
              playsInline
              controls
              preload="metadata"
            />
          </div>
        </div>
      </section>

      {/* FILTER TABS */}
      <div data-testid="service-filter-tabs" className="bg-white/90 backdrop-blur-md border-b border-black/5 sticky top-16 lg:top-[76px] z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 overflow-x-auto">
          <div className="flex gap-1 py-3 min-w-max">
            {filterTabs.map((tab) => (
              <button key={tab.id} data-testid={`filter-tab-${tab.id}`} onClick={() => setActiveFilter(tab.id)}
                className={`rounded-full font-heading font-bold text-xs tracking-[0.08em] uppercase px-4 py-2 transition-colors ${activeFilter === tab.id ? 'bg-[#EE5A01] text-white' : 'text-[#666666] hover:text-[#121212] hover:bg-black/[0.04]'}`}>
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* SERVICE SECTIONS */}
      {show("rental") && <CarRentalSection />}
      {show("leasing") && <LeasingSection />}
      {show("chauffeur") && <ChauffeurSection />}
      {show("coach") && <CoachSection />}
      {show("freight") && <TruckSection />}
      {show("used") && <UsedCarsSection />}

      <CtaBand title="Need a custom solution?" desc="Our team can design a bespoke mobility package for your organisation. Get in touch today."
        ctas={[{ label: 'Contact Our Team', href: '/contact', testid: 'services-contact-btn' }]} />
    </div>
  );
}
